import { inject, Service } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BreadcrumbNavigationPath, BreadcrumbStore } from '@workspace/ng-data-access';
import { filter } from 'rxjs';

@Service()
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly store = inject(BreadcrumbStore);

  constructor() {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.updateBreadcrumbs();
    });

    if (this.router.routerState.snapshot.root) {
      this.updateBreadcrumbs();
    }
  }

  private updateBreadcrumbs(): void {
    const root = this.router.routerState.snapshot.root;
    if (this.hasHideBreadcrumb(root)) {
      this.store.setHidden(true);
      this.store.setPaths([]);
    } else {
      this.store.setHidden(false);
      const paths = this.buildBreadcrumbs(root);
      this.store.setPaths(paths);
    }
  }

  private hasHideBreadcrumb(route: ActivatedRouteSnapshot): boolean {
    if (route.data?.['hideBreadcrumb']) {
      return true;
    }
    for (const child of route.children) {
      if (this.hasHideBreadcrumb(child)) {
        return true;
      }
    }
    return false;
  }

  private buildBreadcrumbs(
    route: ActivatedRouteSnapshot,
    url = '',
    breadcrumbs: BreadcrumbNavigationPath[] = [],
  ): BreadcrumbNavigationPath[] {
    if (route.url.length || route.children.length) {
      const routeURL = route.url.map((segment) => segment.path).join('/');
      if (routeURL) {
        url += `/${routeURL}`;
      }

      // only add breadcrumb if it's explicitly set in route.data
      if (route.data['breadcrumb']) {
        const breadcrumb = route.data['breadcrumb'] as { label: string; path?: string };

        const existing = this.store.paths().find((b) => b.path === (breadcrumb.path ?? url));
        if (existing) {
          breadcrumbs.push(existing); // use the replaced label
        } else {
          // replace params (e.g. :userId)
          let label = breadcrumb.label;
          for (const [key, value] of Object.entries(route.params)) {
            label = label.replace(`:${key}`, value);
          }

          breadcrumbs.push({ label, path: breadcrumb.path ?? url });
        }
      }

      // recurse only into children
      for (const child of route.children) {
        this.buildBreadcrumbs(child, url, breadcrumbs);
      }
    }

    return breadcrumbs;
  }
}
